import { useFormAPI } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import { yearRangePubSub } from "@/widgets/classic-form/form/yearRangePubSub";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context";
import { useSubscribe } from "@/shared/utils/useSubscribe";

interface Params {
    matchAllTermsExactly: boolean;
    matchAllTermsSupported: boolean;
}

/**
 * Subscribes to year-range mode changes and reconciles MAT proximity.
 *
 * Reconciler rule (range → single):
 *   if mode === "single"
 *      && (dateProximity.year === null || dateProximity.year === defaultYearProximity)
 *      && matchAllTermsExactly === true:
 *          set dateProximity to 0 (exact)
 *   otherwise:
 *          leave dateProximity unchanged
 */
export const useOnYearRangeChange = ({ matchAllTermsExactly, matchAllTermsSupported }: Params) => {
    const { getFieldValue, setFieldValue } = useFormAPI();
    const { data: pageContext } = usePageContextQuery();
    const defaultYearProximity = pageContext.runtimeConfig.defaultYearProximity;

    useSubscribe(yearRangePubSub, (fieldPath, mode) => {
        // Only reconcile on range → single transitions
        if (mode === "range") return;

        // Only act when MAT is supported and currently on
        if (!matchAllTermsSupported || !matchAllTermsExactly) return;

        const proximityYearPath = `${fieldPath}.dateProximity.year` as PrefixObjectAccessor<FormValues, []>;
        const proximityMonthPath = `${fieldPath}.dateProximity.month` as PrefixObjectAccessor<FormValues, []>;
        const proximityDayPath = `${fieldPath}.dateProximity.day` as PrefixObjectAccessor<FormValues, []>;

        // Override when proximity is null (Broad) or the configured default — MATE requires Exact
        const currentProximity = getFieldValue(proximityYearPath) as number | null;
        if (currentProximity !== null && currentProximity !== defaultYearProximity) return;

        // Set proximity to exact so MAT stays consistent
        setFieldValue(proximityYearPath, 0);
        setFieldValue(proximityMonthPath, 0);
        setFieldValue(proximityDayPath, 0);
    });
};
